import { useCallback, useEffect, useRef, useState } from 'react'

export interface AsyncState<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

interface Result<T> {
  /** Which request this result belongs to. `null` = nothing loaded yet. */
  key: string | null
  data: T | null
  error: string | null
}

/**
 * Minimal data-loading hook: run once per dependency change, expose
 * {data, error, loading, reload}, abort on unmount. No cache, no query library
 * — M1 has four read endpoints.
 *
 * `loading` is derived (the stored result belongs to an older request key)
 * rather than being a second piece of state flipped inside the effect: that
 * keeps the hook free of cascading renders and free of render-phase ref writes.
 */
export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
): AsyncState<T> {
  const [nonce, setNonce] = useState(0)
  const [result, setResult] = useState<Result<T>>({ key: null, data: null, error: null })
  const requestKey = JSON.stringify([deps, nonce])

  // Callers pass inline arrows, so the loader identity changes every render.
  // The ref is refreshed in an effect (never during render) and the request is
  // keyed on the declared deps instead.
  const loadRef = useRef(load)
  useEffect(() => {
    loadRef.current = load
  })

  useEffect(() => {
    const controller = new AbortController()
    let active = true

    loadRef
      .current(controller.signal)
      .then((value) => {
        if (!active) return
        setResult({ key: requestKey, data: value, error: null })
      })
      .catch((cause: unknown) => {
        if (!active || controller.signal.aborted) return
        setResult({
          key: requestKey,
          data: null,
          error: cause instanceof Error ? cause.message : String(cause),
        })
      })

    return () => {
      active = false
      controller.abort()
    }
  }, [requestKey])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return {
    data: result.key === requestKey ? result.data : null,
    error: result.key === requestKey ? result.error : null,
    loading: result.key !== requestKey,
    reload,
  }
}
